import { BlueprintValue } from '@ekkogmbh/apisdk';
import { Add, Menu, MenuOpen, Remove } from '@mui/icons-material';
import { Divider, IconButton, InputAdornment, TextField } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import TooltipHardened from 'src/Common/Components/TooltipHardened';

interface Props {
  blueprintValue: BlueprintValue;
  onChange: (blueprintValue: BlueprintValue) => void;
  onRemove: () => void;
}

export default function BlueprintValueTextField({ blueprintValue, onChange, onRemove }: Props): React.JSX.Element {
  const [isMetaOpen, setMetaOpen] = useState<boolean>(false);
  const [stagedKey, setStagedKey] = useState<string>('');

  const metadataKeys = Object.keys(blueprintValue.metadata ?? {});

  const amountInput = useMemo(
    () => (
      <TextField
        variant="standard"
        style={{ minWidth: '30px', maxWidth: '110px' }}
        type={'number'}
        label={''}
        inputProps={{ min: 1 }}
        value={blueprintValue.amount}
        onChange={(e) => {
          let numberValue = parseInt(e.target.value);
          if (isNaN(numberValue)) {
            numberValue = 1;
          }
          onChange({ ...blueprintValue, amount: numberValue });
        }}
      />
    ),
    [blueprintValue.amount, onChange],
  );

  const metadataInputs = useMemo(
    () =>
      metadataKeys.map((metaKey, i) => (
        <StyledTextField
          key={i}
          label={metaKey}
          value={blueprintValue.metadata?.[metaKey] ?? ''}
          onChange={(e) => {
            onChange({
              ...blueprintValue,
              metadata: { ...blueprintValue.metadata, [metaKey]: e.target.value as string },
            });
          }}
          onKeyDown={(e) => {
            switch (e.key) {
              case 'Escape':
                setMetaOpen(false);
                break;
            }
          }}
          endAdornment={
            <IconButton
              size={'large'}
              onClick={() => {
                delete blueprintValue.metadata?.[metaKey];
                onChange({ ...blueprintValue });
              }}
            >
              <Remove />
            </IconButton>
          }
        />
      )),
    [metadataKeys, blueprintValue.metadata, onChange],
  );

  const confirmStagedKey = useCallback(() => {
    onChange({ ...blueprintValue, metadata: { ...blueprintValue.metadata, [stagedKey]: '' } });
    setStagedKey('');
  }, [stagedKey, onChange, blueprintValue.metadata]);

  return (
    <StyledTextField
      label={''}
      value={blueprintValue.value}
      onChange={(e) => {
        onChange({ ...blueprintValue, value: e.target.value as string });
      }}
      startAdornment={<InputAdornment position={'start'}>{amountInput}</InputAdornment>}
      endAdornment={
        <TooltipHardened tooltip={'remove value'}>
          <IconButton size={'large'} onClick={onRemove}>
            <Remove />
          </IconButton>
        </TooltipHardened>
      }
      action={{
        icon: metadataKeys.length === 0 ? Menu : MenuOpen,
        onClick() {
          setMetaOpen(!isMetaOpen);
        },
      }}
      collapsible={{
        open: isMetaOpen,
        content: (
          <CenteredColumn width={'100%'}>
            <StyledTextField
              label={'Add Metadata'}
              value={stagedKey}
              onChange={(e) => {
                setStagedKey(e.target.value as string);
              }}
              onKeyDown={(e) => {
                switch (e.key) {
                  case 'Enter':
                    confirmStagedKey();
                    break;
                  case 'Escape':
                    setStagedKey('');
                    setMetaOpen(false);
                    break;
                }
              }}
              endAdornment={
                <IconButton
                  disabled={stagedKey === '' || metadataKeys.includes(stagedKey)}
                  onClick={confirmStagedKey}
                  size={'large'}
                >
                  <Add />
                </IconButton>
              }
            />
            {metadataKeys.length > 0 && <Divider />}
            {metadataInputs}
          </CenteredColumn>
        ),
      }}
    />
  );
}
